"use client";

import { Pause } from "lucide-react";
import { playToggleA11y } from "@/lib/a11y/play-toggle";

/**
 * TPlay — §03.4 "Play button" (03-Audio.dc.html)
 *
 * "Seven sizes, one component." This is that component. There used to be two:
 * `play-btn.tsx` carried a 44px default and a third resting treatment the spec
 * does not have (`--tls-surface-elevated` under a `--tls-border-strong` edge),
 * and the design-system preview rendered it beside this one, documenting the
 * inconsistency as though it were intentional (TES-2310).
 *
 * The ladder §03.4 names, and the only status a user needs at a glance:
 *
 *   ring   1.5px ink on transparent — the track is not loaded yet
 *   ink    solid `--tls-fg-strong`  — loaded, the next press is instant
 *   accent solid `--tls-brand`      — playing right now
 *
 * The detail page painted the ring on a ready control until TES-2136, so the
 * one action the page exists to sell read as the quietest element on the fold
 * while the Download carried the only fill. The ring had been reached for to
 * avoid "a second filled primary beside the accent Download pill", which
 * conflates *filled* with *accent*: §01.2's one-primary-per-panel rule governs
 * the six ACCENT roles, and ink spends none of them. The ink fill therefore
 * costs nothing against the 8% area cap and leaves the Download as the panel's
 * single accent primary. Do not restore the ring to buy accent budget.
 *
 * Buffering keeps whichever fill it started from rather than dropping to the
 * ring: §03.4 requires the control to report what you did on pointerdown, not
 * what the audio did, so `loading` reaches `aria-busy` only.
 *
 * No shadow. Those belong to the four floating surfaces (sheet, modal, mini
 * player, consent), never to a control on a page panel. The border is declared
 * in every state, transparent when filled, so the border-box geometry is
 * identical across the ladder and the disc never resizes as state changes.
 */

/**
 * §03.4's size ladder is 28 · 40 · 44 · 52 · 56 · 64 · 76, and `size` stays a
 * plain number rather than a union of those seven: the mini player's 28 is
 * admitted only because the whole bar is also tappable, which a type cannot
 * check. 44 is the default because it is the WCAG 2.5.5 floor.
 */
const LADDER_DEFAULT = 44;

interface TPlayProps {
  playing: boolean;
  onClick?: () => void;
  /** One of §03.4's seven sizes. Defaults to 44, the WCAG 2.5.5 floor. */
  size?: number;
  /**
   * False paints §03.4's 1.5px ring — "not loaded yet". Defaults to true: a
   * control that has resolved its audio is the common case, and the ring on a
   * ready control is exactly the regression TES-2136 fixed.
   */
  loaded?: boolean;
  /** Subject of the control (normally the track title) for the stable name. */
  title?: string;
  /** True while the track is resolving or buffering. Carried by aria-busy only. */
  loading?: boolean;
}

export function TPlay({
  onClick,
  playing,
  size = LADDER_DEFAULT,
  loaded = true,
  title,
  loading,
}: TPlayProps) {
  // §03.4's three-way ladder, resolved once.
  const paint = playing
    ? { fill: "var(--tls-brand)", glyph: "var(--tls-brand-fg)", ring: "transparent" }
    : loaded
      ? { fill: "var(--tls-fg-strong)", glyph: "var(--tls-canvas)", ring: "transparent" }
      : { fill: "transparent", glyph: "var(--tls-fg-strong)", ring: "var(--tls-fg-strong)" };

  return (
    <button type="button"
      onClick={onClick}
      // Playback state is exposed via aria-pressed with a stable name (TES-1581).
      {...playToggleA11y({ playing, title, loading })}
      // data-icon-btn opts out of the global @media (pointer: coarse) button
      // min-height rule, which only stretches height and would render an oval
      // hit-target on a control whose width is its `size`. The square 44px
      // floor below is how this component pays WCAG 2.5.5 instead: a caller
      // asking for 28 still gets a 44x44 target, because min-width/min-height
      // win over the smaller width/height, while the glyph stays proportioned
      // to `size`.
      data-icon-btn
      className="motion-safe:active:scale-[0.97] tlsr-focus"
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      style={{
        width: size,
        height: size,
        minWidth: "var(--touch-target-min, 44px)",
        minHeight: "var(--touch-target-min, 44px)",
        aspectRatio: "1 / 1",
        padding: 0,
        borderRadius: "50%",
        background: paint.fill,
        color: paint.glyph,
        border: `1.5px solid ${paint.ring}`,
        boxShadow: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "scale var(--tls-duration-fast) var(--tls-ease)",
        position: "relative",
      }}
    >
      {playing ? (
        <Pause style={{ width: size * 0.36, height: size * 0.36 }} />
      ) : (
        // Tesla-style T-shaped play (vertical stroke + crossbar + triangle).
        // The path's own left inset carries §17.11's optical offset, so this
        // glyph needs no playGlyphOffset margin the way a lucide Play does.
        <svg width={size * 0.4} height={size * 0.4} viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5l12 7-12 7V5z" />
        </svg>
      )}
    </button>
  );
}
